import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, RegisterDto } from '../services/auth.service';
import { User, LoginResponse } from '../interfaces/user.interface';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: User | null;
  login: (loginResponse: LoginResponse) => Promise<void>;
  register: (userData: RegisterDto) => Promise<void>;
  refreshSession: () => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  register: async () => {},
  refreshSession: async () => {},
  logout: () => {},
  loading: true,
  isAuthenticated: false,
});

function toUser(loginResponse: LoginResponse): User {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    access_token,
    refresh_token,
    requiresTwoFactor,
    tempToken,
    message,
    ...userData
  } = loginResponse;
  /* eslint-enable @typescript-eslint/no-unused-vars */
  return userData as User;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authService.getProfile({ skipAuthRedirect: true, skipErrorDialog: true })
      .then((profile) => setUser(profile))
      .catch(() => {
        // Not authenticated, ignore error
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (loginResponse: LoginResponse) => {
    setUser(toUser(loginResponse));
  };

  const register = async (userData: RegisterDto) => {
    const response = await authService.register(userData);
    await login(response);
  };

  const refreshSession = async () => {
    const profile = await authService.getProfile({
      skipAuthRedirect: false,
      skipErrorDialog: true,
    });
    setUser(profile);
  };

  const logout = () => {
    authService.logout()
      .catch((error) => {
        logger.error('Logout failed:', error);
      })
      .finally(() => {
        setUser(null);
        window.location.href = '/login';
      });
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      refreshSession,
      logout,
      loading,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
